/**
 * Authoritative payroll week definition — single source of truth for week boundaries.
 * Week start day comes from organization settings (`payroll.week_starts_on`).
 */

import {
  DEFAULT_ORG_SETTINGS,
  getEffectiveOrgSetting,
} from "./organizationSettings";

export const PAYROLL_WEEK_STARTS_ON = Object.freeze({
  SUNDAY: "sunday",
  MONDAY: "monday",
});

/** Default when org has no stored preference — matches legacy KPI / payrollHelpers. */
export const DEFAULT_PAYROLL_WEEK_STARTS_ON = PAYROLL_WEEK_STARTS_ON.SUNDAY;

const VALID_WEEK_STARTS_ON = new Set(Object.values(PAYROLL_WEEK_STARTS_ON));

export function normalizePayrollWeekStartsOn(value) {
  const normalized = String(value || "").toLowerCase();

  if (VALID_WEEK_STARTS_ON.has(normalized)) {
    return normalized;
  }

  return DEFAULT_PAYROLL_WEEK_STARTS_ON;
}

/**
 * Resolve configured week start from org settings.
 * Supports `payroll.week_starts_on` and legacy `payroll.week_start_day`.
 */
export function resolvePayrollWeekStartsOn(orgSettings) {
  const source = orgSettings || DEFAULT_ORG_SETTINGS;

  const configured =
    getEffectiveOrgSetting(source, "payroll.week_starts_on", undefined) ??
    getEffectiveOrgSetting(source, "payroll.week_start_day", undefined);

  if (configured !== undefined) {
    return normalizePayrollWeekStartsOn(configured);
  }

  return DEFAULT_PAYROLL_WEEK_STARTS_ON;
}

export function toLocalDateIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeReferenceDate(referenceDate) {
  const date = new Date(referenceDate);
  date.setHours(12, 0, 0, 0);

  return date;
}

/**
 * Start of the payroll week containing the reference date (local calendar).
 */
export function startOfPayrollWeek(
  referenceDate = new Date(),
  weekStartsOn = DEFAULT_PAYROLL_WEEK_STARTS_ON,
) {
  const date = normalizeReferenceDate(referenceDate);
  const day = date.getDay();
  const startDay = normalizePayrollWeekStartsOn(weekStartsOn);

  const diff =
    startDay === PAYROLL_WEEK_STARTS_ON.SUNDAY
      ? -day
      : day === 0
        ? -6
        : 1 - day;

  date.setDate(date.getDate() + diff);

  return date;
}

/**
 * End of the payroll week containing the reference date (local calendar).
 */
export function endOfPayrollWeek(
  referenceDate = new Date(),
  weekStartsOn = DEFAULT_PAYROLL_WEEK_STARTS_ON,
) {
  const end = startOfPayrollWeek(referenceDate, weekStartsOn);
  end.setDate(end.getDate() + 6);

  return end;
}

/**
 * Full payroll week bounds for the week containing the reference date.
 * Metrics consumers typically query weekStartStr through todayStr (inclusive).
 */
export function getPayrollWeekBounds(
  referenceDate = new Date(),
  weekStartsOn = DEFAULT_PAYROLL_WEEK_STARTS_ON,
) {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);

  const weekStart = startOfPayrollWeek(referenceDate, weekStartsOn);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return {
    weekStart,
    weekEnd,
    weekStartStr: toLocalDateIso(weekStart),
    weekEndStr: toLocalDateIso(weekEnd),
    todayStr: toLocalDateIso(today),
  };
}

export function getPayrollWeekBoundsFromSettings(
  referenceDate = new Date(),
  orgSettings,
) {
  return getPayrollWeekBounds(
    referenceDate,
    resolvePayrollWeekStartsOn(orgSettings),
  );
}

/**
 * Payroll week bounds offset by whole weeks (negative = past).
 */
export function getOffsetPayrollWeekBounds(
  referenceDate = new Date(),
  weekStartsOn = DEFAULT_PAYROLL_WEEK_STARTS_ON,
  weekOffset = 0,
) {
  const shifted = new Date(referenceDate);
  shifted.setDate(shifted.getDate() + weekOffset * 7);

  return {
    weekStart: startOfPayrollWeek(shifted, weekStartsOn),
    weekEnd: endOfPayrollWeek(shifted, weekStartsOn),
  };
}

export function formatWeekRangeLabel(weekStartStr, weekEndStr) {
  const fmt = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-CA", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return `${fmt(weekStartStr)} – ${fmt(weekEndStr)}`;
}

/** @deprecated Use getPayrollWeekBounds(date, PAYROLL_WEEK_STARTS_ON.SUNDAY) */
export function getSundayWeekBounds(referenceDate = new Date()) {
  return getPayrollWeekBounds(referenceDate, PAYROLL_WEEK_STARTS_ON.SUNDAY);
}
